import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { TaskEditorValue, TaskFieldErrors } from '@features/Admin/Agent/taskFlow/domain/contracts';
import { TASK_TYPE_REGISTRY } from '@features/Admin/Agent/taskFlow/domain/taskTypeRegistry';
import {
  parseCollectDataSchema,
  SchemaValidationError,
} from '@features/Admin/Agent/taskFlow/domain/schema-validation';

type TaskFlowDrawerProps = {
  open: boolean;
  task: TaskEditorValue | null;
  errors: TaskFieldErrors;
  disabled: boolean;
  onClose: () => void;
  onUpdateTask: (clientKey: string, updater: (task: TaskEditorValue) => TaskEditorValue) => void;
  onDeleteTask: (clientKey: string) => void;
  onPendingDraftChange?: (dirty: boolean) => void;
};

type SchemaDraft = {
  properties: Record<string, { type?: string; description?: string }>;
  required?: string[];
};

const CollectDataSchemaEditor: React.FC<{
  task: TaskEditorValue;
  disabled: boolean;
  onChange: (updater: (task: TaskEditorValue) => TaskEditorValue) => void;
  onPendingDraftChange?: (dirty: boolean) => void;
}> = ({ task, disabled, onChange, onPendingDraftChange }) => {
  const { t } = useTranslation();
  const [rawMode, setRawMode] = useState(false);
  const [rawError, setRawError] = useState<string | null>(null);
  const schema = useMemo<SchemaDraft>(
    () =>
      (task.schema as SchemaDraft | null | undefined) ?? {
        properties: {},
        required: [],
      },
    [task.schema],
  );
  const properties = schema.properties ?? {};
  const required = new Set(schema.required ?? []);
  const [rawText, setRawText] = useState(() => JSON.stringify(schema, null, 2));
  useEffect(() => {
    onPendingDraftChange?.(rawMode && rawText !== JSON.stringify(schema, null, 2));
  }, [rawMode, rawText, schema, onPendingDraftChange]);
  useEffect(() => () => onPendingDraftChange?.(false), [onPendingDraftChange]);

  useEffect(() => {
    setRawText(JSON.stringify(schema, null, 2));
    setRawError(null);
  }, [schema]);

  const getSchemaError = (error: SchemaValidationError) =>
    t(`assistantsPage.modals.taskFlow.errors.${error}`);

  const commitSchema = (next: SchemaDraft) => {
    onChange((previous) => ({ ...previous, schema: next as TaskEditorValue['schema'] }));
  };

  const addProperty = () => {
    let index = Object.keys(properties).length + 1;
    let name = `field_${index}`;
    while (name in properties) name = `field_${++index}`;
    commitSchema({
      ...schema,
      properties: { ...properties, [name]: { type: 'string' } },
    });
  };

  const renameProperty = (oldName: string, newName: string) => {
    if (!newName.trim() || newName === oldName || newName in properties) return;
    const next = { ...properties, [newName]: properties[oldName] };
    delete next[oldName];
    const nextRequired = required.has(oldName)
      ? (schema.required ?? []).map((name) => (name === oldName ? newName : name))
      : schema.required;
    commitSchema({ ...schema, properties: next, required: nextRequired });
  };

  const updateProperty = (name: string, patch: { type?: string; description?: string }) => {
    commitSchema({
      ...schema,
      properties: { ...properties, [name]: { ...properties[name], ...patch } },
    });
  };

  const toggleRequired = (name: string) => {
    const next = required.has(name)
      ? (schema.required ?? []).filter((value) => value !== name)
      : [...(schema.required ?? []), name];
    commitSchema({ ...schema, required: next });
  };

  const removeProperty = (name: string) => {
    const next = { ...properties };
    delete next[name];
    commitSchema({
      ...schema,
      properties: next,
      required: (schema.required ?? []).filter((value) => value !== name),
    });
  };

  if (rawMode) {
    return (
      <Stack spacing={1}>
        <TextField
          label={t('assistantsPage.modals.taskFlow.schemaRaw')}
          value={rawText}
          onChange={(event) => setRawText(event.target.value)}
          multiline
          minRows={8}
          fullWidth
          error={Boolean(rawError)}
          helperText={rawError ?? t('assistantsPage.modals.taskFlow.schemaHelper')}
          disabled={disabled}
        />
        <Stack direction="row" spacing={1}>
          <Button
            type="button"
            size="small"
            disabled={disabled}
            onClick={() => {
              const result = parseCollectDataSchema(rawText);
              if (result.valid) {
                setRawError(null);
                commitSchema(result.schema);
                setRawMode(false);
              } else {
                setRawError(getSchemaError(result.error));
              }
            }}>
            {t('assistantsPage.modals.taskFlow.apply')}
          </Button>
          <Button type="button" size="small" onClick={() => setRawMode(false)} disabled={disabled}>
            {t('assistantsPage.modals.cancel')}
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">
          {t('assistantsPage.modals.taskFlow.fieldsToCollect')}
        </Typography>
        <Button
          type="button"
          size="small"
          onClick={() => {
            setRawText(JSON.stringify(schema, null, 2));
            setRawMode(true);
          }}
          disabled={disabled}>
          {t('assistantsPage.modals.taskFlow.editAsJson')}
        </Button>
      </Stack>
      {Object.entries(properties).map(([name, property]) => (
        <Stack
          key={name}
          spacing={0.75}
          sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              label={t('assistantsPage.modals.taskFlow.propertyName')}
              value={name}
              disabled={disabled}
              onChange={(event) => renameProperty(name, event.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              select
              label={t('assistantsPage.modals.taskFlow.type')}
              value={property.type ?? 'string'}
              disabled={disabled}
              SelectProps={{
                MenuProps: {
                  sx: { zIndex: (theme) => theme.zIndex.modal + 3 },
                },
              }}
              onChange={(event) => updateProperty(name, { type: event.target.value })}
              sx={{ minWidth: 130 }}>
              {['string', 'integer', 'number', 'boolean'].map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={required.has(name)}
                  disabled={disabled}
                  onChange={() => toggleRequired(name)}
                />
              }
              label={t('assistantsPage.modals.taskFlow.required')}
            />
            <IconButton
              size="small"
              aria-label={t('assistantsPage.modals.taskFlow.removeProperty', { name })}
              onClick={() => removeProperty(name)}
              disabled={disabled}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
          <TextField
            size="small"
            label={t('assistantsPage.modals.taskFlow.descriptionOptional')}
            value={property.description ?? ''}
            disabled={disabled}
            onChange={(event) => updateProperty(name, { description: event.target.value })}
          />
        </Stack>
      ))}
      <Button
        type="button"
        size="small"
        onClick={addProperty}
        disabled={disabled}
        sx={{ alignSelf: 'flex-start' }}>
        {t('assistantsPage.modals.taskFlow.addField')}
      </Button>
    </Stack>
  );
};

export const TaskFlowDrawer: React.FC<TaskFlowDrawerProps> = ({
  open,
  task,
  errors,
  disabled,
  onClose,
  onUpdateTask,
  onDeleteTask,
  onPendingDraftChange,
}) => {
  const { t } = useTranslation();
  if (!task) return null;
  const meta = TASK_TYPE_REGISTRY[task.type];
  const update = (updater: (value: TaskEditorValue) => TaskEditorValue) =>
    onUpdateTask(task.clientKey, updater);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      slotProps={{ backdrop: { sx: { backgroundColor: 'transparent' } } }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 460 },
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {meta ? t(`assistantsPage.modals.taskFlow.${meta.labelKey}`) : task.type}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('assistantsPage.modals.taskFlow.typeLocked')}
          </Typography>
        </Box>
        <IconButton
          data-testid="task-flow-drawer-close"
          size="small"
          onClick={onClose}
          aria-label={t('assistantsPage.modals.taskFlow.closeEditor')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <TextField
        label={t('assistantsPage.modals.taskFlow.taskId')}
        value={task.id}
        disabled={disabled}
        error={Boolean(errors.id)}
        helperText={errors.id ? t(`assistantsPage.modals.taskFlow.errors.${errors.id}`) : undefined}
        onChange={(event) => update((value) => ({ ...value, id: event.target.value }))}
        fullWidth
        size="small"
        autoFocus
      />
      <TextField
        label={t('assistantsPage.modals.taskFlow.description')}
        value={task.description}
        disabled={disabled}
        error={Boolean(errors.description)}
        helperText={
          errors.description
            ? t(`assistantsPage.modals.taskFlow.errors.${errors.description}`)
            : undefined
        }
        onChange={(event) => update((value) => ({ ...value, description: event.target.value }))}
        fullWidth
        multiline
        minRows={2}
        size="small"
      />
      <TextField
        label={t('assistantsPage.modals.taskFlow.instructionsOptional')}
        value={task.instructions ?? ''}
        disabled={disabled}
        error={Boolean(errors.instructions)}
        helperText={
          errors.instructions
            ? t(`assistantsPage.modals.taskFlow.errors.${errors.instructions}`)
            : t('assistantsPage.modals.taskFlow.instructionsHelper')
        }
        onChange={(event) => update((value) => ({ ...value, instructions: event.target.value }))}
        fullWidth
        multiline
        minRows={2}
        size="small"
      />

      {meta?.fieldSet === 'collectData' && (
        <CollectDataSchemaEditor
          key={task.clientKey}
          task={task}
          disabled={disabled}
          onChange={update}
          onPendingDraftChange={onPendingDraftChange}
        />
      )}

      {meta?.fieldSet === 'dtmf' && (
        <Stack spacing={1.5}>
          <TextField
            label={t('assistantsPage.modals.taskFlow.numDigits')}
            type="number"
            inputProps={{ min: 1, max: 32, step: 1 }}
            value={task.numDigits ?? ''}
            disabled={disabled}
            error={Boolean(errors.numDigits)}
            helperText={
              errors.numDigits
                ? t(`assistantsPage.modals.taskFlow.errors.${errors.numDigits}`)
                : t('assistantsPage.modals.taskFlow.numDigitsHelper')
            }
            onChange={(event) =>
              update((value) => ({
                ...value,
                numDigits: event.target.value === '' ? null : Number(event.target.value),
              }))
            }
            size="small"
          />
          <TextField
            label={t('assistantsPage.modals.taskFlow.inputTimeout')}
            type="number"
            inputProps={{ min: 1, max: 60, step: 'any' }}
            value={task.dtmfInputTimeout ?? ''}
            disabled={disabled}
            error={Boolean(errors.dtmfInputTimeout)}
            helperText={
              errors.dtmfInputTimeout
                ? t(`assistantsPage.modals.taskFlow.errors.${errors.dtmfInputTimeout}`)
                : t('assistantsPage.modals.taskFlow.inputTimeoutHelper')
            }
            onChange={(event) =>
              update((value) => ({
                ...value,
                dtmfInputTimeout:
                  event.target.value === '' ? undefined : Number(event.target.value),
              }))
            }
            size="small"
          />
          <TextField
            select
            label={t('assistantsPage.modals.taskFlow.stopEvent')}
            value={task.dtmfStopEvent ?? ''}
            disabled={disabled}
            SelectProps={{
              MenuProps: {
                sx: { zIndex: (theme) => theme.zIndex.modal + 3 },
              },
            }}
            error={Boolean(errors.dtmfStopEvent)}
            helperText={
              errors.dtmfStopEvent
                ? t(`assistantsPage.modals.taskFlow.errors.${errors.dtmfStopEvent}`)
                : t('assistantsPage.modals.taskFlow.stopEventHelper')
            }
            onChange={(event) =>
              update((value) => ({ ...value, dtmfStopEvent: event.target.value }))
            }
            size="small">
            <MenuItem value="">{t('common.none')}</MenuItem>
            {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'].map((symbol) => (
              <MenuItem key={symbol} value={symbol}>
                {symbol}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(task.askForConfirmation)}
                disabled={disabled}
                onChange={(event) =>
                  update((value) => ({ ...value, askForConfirmation: event.target.checked }))
                }
              />
            }
            label={t('assistantsPage.modals.taskFlow.askForConfirmation')}
          />
        </Stack>
      )}

      <Box sx={{ flex: 1, minHeight: 12 }} />
      <Button
        type="button"
        color="error"
        variant="outlined"
        startIcon={<DeleteOutlineIcon />}
        disabled={disabled}
        onClick={() => {
          onDeleteTask(task.clientKey);
          onClose();
        }}>
        {t('assistantsPage.modals.taskFlow.delete')}
      </Button>
    </Drawer>
  );
};
